/**
 * Tiny user-scoped localStorage snapshot of the plan/enforcement + subscription
 * queries.
 *
 * WHY: after a page reload React Query starts cold (no persistence), so the tier
 * plan + "can optimize" state only appears once the auth -> check_can_optimize
 * RPC waterfall finishes. useEnforcement / useSubscription seed React Query's
 * `initialData` from this snapshot so the last-known plan renders INSTANTLY on
 * reload, then revalidates in the background (refetchOnMount).
 *
 * Scoped by userId and cleared on sign-out / user switch (see AuthContext) so one
 * user's plan never lingers for the next user on a shared browser. We only ever
 * store the small, non-sensitive plan/subscription rows here — nothing else.
 */
const PREFIX = 'feed-optimizer-plan-cache';
/**
 * VERSION 2 (Bestandsmass, 2026-08-20): `check_can_optimize` liefert seither
 * eine andere Zeile — `managed_count` / `remaining_capacity` sind dazugekommen,
 * und `useEnforcement.count` liest jetzt den BESTAND statt des
 * Lifetime-Verbrauchs.
 *
 * Die Erhoehung ist kein Formalismus: ein v1-Snapshot aus der Verbrauchs-Aera
 * traegt kein `managed_count`, wuerde beim Seeding also als `count = 0` und
 * `remaining_capacity = limit` einspielen — ein volles Konto saehe nach dem
 * Reload einen Moment lang leer aus, samt gruener Phase und freigeschalteten
 * Knoepfen. Ein verworfener Snapshot kostet dagegen nur den einen RPC-Umlauf,
 * den es vor der Cache-Einfuehrung ohnehin brauchte.
 */
/**
 * VERSION 3 (Preisumbau Kapazitaets-Baender, 2026-08-21): `plan_tier` kann
 * seither `cap500` … `cap100000` sein, und die Subscription-Zeile traegt die
 * Add-on-Spalten `addon_mc_booked` / `addon_language_booked`. Ein v2-Snapshot
 * aus der 6-Tier-Aera wuerde beim Seeding einen Tarif zeigen, den es nicht
 * mehr zu kaufen gibt, und die Add-on-Zeilen in den Einstellungen leer
 * erscheinen lassen. Ein verworfener Snapshot kostet nur den einen
 * RPC-Umlauf des Erst-Loads.
 */
const VERSION = 3;

type Envelope<T> = { v: number; t: number; data: T };

function keyFor(kind: string, userId: string): string {
  return `${PREFIX}:${kind}:${userId}`;
}

/**
 * Read the snapshot for a given kind ('enforcement' | 'subscription') + user.
 * Returns the data plus the timestamp it was written (for `initialDataUpdatedAt`,
 * so React Query knows whether the seed is stale and needs a background refetch).
 * Never throws — corrupt/absent data resolves to undefined.
 */
export function readPlanCache<T>(
  kind: string,
  userId: string | undefined,
): { data: T; updatedAt: number } | undefined {
  if (!userId || typeof localStorage === 'undefined') return undefined;
  try {
    const raw = localStorage.getItem(keyFor(kind, userId));
    if (!raw) return undefined;
    const env = JSON.parse(raw) as Envelope<T>;
    if (!env || env.v !== VERSION || env.data == null) return undefined;
    return { data: env.data, updatedAt: typeof env.t === 'number' ? env.t : 0 };
  } catch {
    return undefined;
  }
}

/** Persist a snapshot. No-ops for a missing user, null data, or unavailable/full storage. */
export function writePlanCache<T>(kind: string, userId: string | undefined, data: T): void {
  if (!userId || typeof localStorage === 'undefined' || data == null) return;
  try {
    const env: Envelope<T> = { v: VERSION, t: Date.now(), data };
    localStorage.setItem(keyFor(kind, userId), JSON.stringify(env));
  } catch {
    // localStorage full or disabled — losing the instant-reload optimization is
    // harmless, the queries still fetch normally.
  }
}

/** Remove every plan-cache key. Called on sign-out / user switch. */
export function clearPlanCache(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) localStorage.removeItem(k);
    }
  } catch {
    // ignore
  }
}
